import { PropsWithChildren } from 'react';
import {
  alpha,
  Box,
  Button,
  Grid,
  Link,
  RadioGroup,
  Skeleton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, RadioBox } from '@components';
import PrimarySecondary from '@common/ui/PrimarySecondary';
import { routes } from '@routes';
import { generatePath, RouterLink } from '@libs/router';
import { useIsIntegrationCanBeUsed } from '@hooks';
import { IntegrationStatus } from '@api/apn-core/api';

const SelectorWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.active, 0.48),
  borderRadius: theme.shape.borderRadius,
}));

const StyledRadioBox = styled(RadioBox)({
  width: '100%',

  '& .MuiFormControlLabel-label': {
    width: '100%',
    marginLeft: '10px',
    display: 'block',
  },
});

interface CreateReportIntegrationSelectorProps {
  selectedIntegrationId: string;
  onIntegrationSelect: (value: string) => void;
  isLoading?: boolean;
}

export default function CreateReportIntegrationSelector({
  selectedIntegrationId,
  onIntegrationSelect,
  isLoading,
  children,
}: PropsWithChildren<CreateReportIntegrationSelectorProps>) {
  return (
    <SelectorWrapper>
      <Stack direction="column" spacing={2}>
        <Typography variant="subtitle2">Select Integration</Typography>
        <RadioGroup value={selectedIntegrationId} onChange={(event) => onIntegrationSelect(event.target.value)}>
          <Grid container spacing={2}>
            {isLoading ? <CreateReportIntegrationSelectorSkeletons /> : children}
          </Grid>
        </RadioGroup>
      </Stack>
    </SelectorWrapper>
  );
}

interface CreateReportIntegrationItemProps {
  id: string;
  name: string;
  icon: string;
  resourceType: string;
  status: IntegrationStatus;
  resourceCount?: number;
  isLoading?: boolean;
}

export const CreateReportIntegrationItem = ({
  id,
  name,
  icon,
  resourceType,
  status,
  resourceCount,
  isLoading,
}: CreateReportIntegrationItemProps) => {
  const isUsableIntegration = useIsIntegrationCanBeUsed();

  const path = `${routes.Inventory.path}?query=${encodeURIComponent(
    `integration= "${id}" AND resource_type = "${resourceType}"`,
  )}`;

  const disabled = resourceCount === 0 || !isUsableIntegration(status);

  return (
    <Grid item xs={4}>
      <StyledRadioBox
        value={id}
        disabled={disabled}
        label={
          <Tooltip
            title={disabled ? `This integration is in error. Learn more in the Integrations page.` : ''}
            placement="top"
            arrow
          >
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" py={1}>
              <PrimarySecondary icon={icon} primary={name} maxWidth={200} />
              {isLoading ? (
                <Skeleton variant="text" width={100} />
              ) : (
                <Link component={RouterLink} to={path} variant="helperText" noWrap>
                  {resourceCount ? `${resourceCount} Accounts` : 'No Accounts'}
                </Link>
              )}
            </Stack>
          </Tooltip>
        }
      />
    </Grid>
  );
};

const CreateReportIntegrationSelectorSkeletons = () => (
  <>
    {[...Array(6)].map((_, index) => (
      <Grid item xs={4} key={index}>
        <StyledRadioBox
          disabled
          label={
            <Stack direction="row" spacing={1} py={1}>
              <Skeleton variant="rounded" width={24} height={24} />
              <Skeleton variant="text" width={100} />
            </Stack>
          }
        />
      </Grid>
    ))}
  </>
);

export function CreateReportIntegrationSelectorEmptyState() {
  const path = generatePath(routes.ConnectIntegrationsGroup.path, {
    group: 'amazon-web-services',
  });

  return (
    <Stack direction="row" alignItems="center" justifyContent="center" p={2} width="100%">
      <EmptyState
        imgSrc="/static/EmptyStateImages/not-found-illustration.svg"
        title="No AWS Organization integration connected yet"
        action={
          <Button component={RouterLink} to={`${path}?fromStandingAccessReport=1`} variant="outlined">
            Connect AWS Organization
          </Button>
        }
      />
    </Stack>
  );
}
